import { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Archive, Loader2 } from 'lucide-react';
import { exportTemplateToBlob } from '../lib/exportTemplate';
import { toast } from 'sonner@2.0.3';
import JSZip from 'jszip';
import type { Account } from '../../../entities/account';
import { useTemplateStore } from '../../../entities/template';

interface ExportZipButtonProps {
  /**
   * Выбранные аккаунты для экспорта
   */
  checkedAccounts: Account[];

  /**
   * Ручные размеры шрифта по аккаунтам (accountId → кегль в preview-space px).
   * Нет записи — размер подбирается автоматически, ровно как в превью.
   */
  fontSizeOverrides?: Map<string, number>;
  
  /**
   * URL изображения (общий для всех)
   */
  imageUrl?: string;
  
  /**
   * Текст поста (общий для всех)
   */
  text?: string;
  
  /**
   * Режим отображения медиа (общий для всех)
   */
  fitMode?: 'fill' | 'fit';
  
  /**
   * Ref к PostTemplate для измерения реального размера
   */
  templateRef?: React.RefObject<HTMLDivElement>;
  
  /**
   * Размер кнопки
   */
  size?: 'sm' | 'default' | 'lg';
  
  /**
   * Вариант кнопки
   */
  variant?: 'default' | 'outline' | 'secondary' | 'ghost';
  
  /**
   * Полная ширина
   */
  fullWidth?: boolean;
}

export function ExportZipButton({
  checkedAccounts,
  imageUrl,
  text,
  fitMode,
  templateRef,
  size = 'default',
  variant = 'outline',
  fullWidth = false,
  fontSizeOverrides,
}: ExportZipButtonProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const { templates } = useTemplateStore();

  const handleExportZip = async () => {
    if (checkedAccounts.length === 0) {
      toast.error('Выберите хотя бы один аккаунт');
      return;
    }

    setIsExporting(true);

    try {
      // Измеряем реальный размер PostTemplate
      let sourceWidth = 400; // значение по умолчанию (max-w-md)
      
      if (templateRef?.current) {
        const rect = templateRef.current.getBoundingClientRect();
        sourceWidth = rect.width;
        console.log('📏 Measured template width:', sourceWidth);
      }

      const zip = new JSZip();
      let exportedCount = 0;
      let skippedCount = 0;
      
      console.log(`🗜️ Создание ZIP архива для ${checkedAccounts.length} аккаунтов...`);

      // Экспортируем пост для каждого аккаунта
      for (let i = 0; i < checkedAccounts.length; i++) {
        const account = checkedAccounts[i];
        
        // Обновляем прогресс
        const currentProgress = Math.round(((i + 1) / checkedAccounts.length) * 100);
        setProgress(currentProgress);
        
        console.log(`📦 Экспорт ${i + 1}/${checkedAccounts.length}: ${account.username}`);
        
        // Получаем шаблон аккаунта по accountId (как в CreatePostForm)
        const template = templates.find(t => t.accountId === account.id && (t.type ?? 'reel') === 'post');
        
        if (!template) {
          console.warn(`⚠️ Аккаунт ${account.username} не имеет шаблона, пропускаем`);
          skippedCount++;
          continue;
        }
        
        console.log(`✅ Используем шаблон: ${template.name} для ${account.username}`);
        
        const blob = await exportTemplateToBlob({
          backgroundColor: template.backgroundColor,
          useGradient: template.useGradient,
          backgroundColorEnd: template.backgroundColorEnd,
          backgroundAlpha: template.backgroundAlpha,
          backgroundAlphaEnd: template.backgroundAlphaEnd,
          textColor: template.textColor,
          useTwoColors: template.useTwoColors,
          textColorSecondary: template.textColorSecondary,
          fontFamily: template.fontFamily,
          fontUrl: template.fontUrl,
          fontName: template.fontName,
          // Тот же источник размера, что и у превью с одиночной выгрузкой:
          // ручной размер этого аккаунта, иначе undefined — и рендер подбирает
          // кегль сам. Шаблонный fontSize сюда не идёт: у 40 шаблонов из 50 он
          // пуст, а у остальных давно разошёлся с тем, что видно на экране.
          fontSize: fontSizeOverrides?.get(account.id),
          logoUrl: template.logoUrl,
          logoPosition: template.logoPosition,
          logoSize: template.logoSize,
          textLineSpacing: template.textLineSpacing,
          imageUrl,
          text,
          textUppercase: template.textUppercase,
          textAlign: template.textAlign,
          fontBold: template.fontBold,
          fitMode,
          showLine: template.showLine,
          lineColor: template.lineColor,
          lineHeight: template.lineHeight,
          linePosition: template.linePosition,
          textShadowEnabled: template.textShadowEnabled,
          textShadowColor: template.textShadowColor,
          textShadowBlur: template.textShadowBlur,
          textShadowWidth: template.textShadowWidth,
          textSoftShadowEnabled: template.textSoftShadowEnabled,
          textSoftShadowColor: template.textSoftShadowColor,
          textSoftShadowBlur: template.textSoftShadowBlur,
          textSoftShadowOffsetX: template.textSoftShadowOffsetX,
          textSoftShadowOffsetY: template.textSoftShadowOffsetY,
          textSoftShadowOpacity: template.textSoftShadowOpacity,
          textShadowOpacity: template.textShadowOpacity,
          postLayout: template.postLayout,
          accentColor: template.accentColor,
          cardColor: template.cardColor,
          cardInset: template.cardInset,
          bandHeight: template.bandHeight,
          frameUrl: template.frameUrl,
          frameTextX: template.frameTextX,
          frameTextY: template.frameTextY,
          frameTextWidth: template.frameTextWidth,
          frameTextVAlign: template.frameTextVAlign,
          autoFontMax: template.autoFontMax,
          bandGrow: template.bandGrow,
          sourceWidth,
        });

        // Добавляем файл в архив с именем аккаунта
        const fileName = `${account.username}.png`;
        zip.file(fileName, blob);
        exportedCount++;
      }

      console.log(`✅ Экспортировано: ${exportedCount}, пропущено: ${skippedCount}`);

      // Проверяем что хотя бы один пост экспортирован
      if (exportedCount === 0) {
        toast.error('Не удалось экспортировать ни одного поста. Проверьте что у аккаунтов установлены шаблоны.');
        return;
      }

      // Генерируем ZIP архив
      const zipBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: {
          level: 6 // Средний уровень сжатия для баланса скорости/размера
        }
      });

      // Скачиваем ZIP файл
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `instagram-posts-${Date.now()}.zip`;
      
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('✅ ZIP архив успешно скачан!');
      
      // Формируем сообщение об успехе
      let message = `ZIP архив с ${exportedCount} постами успешно скачан!`;
      if (skippedCount > 0) {
        message += ` (${skippedCount} пропущено - нет шаблона)`;
      }
      toast.success(message);
    } catch (error) {
      console.error('❌ Export ZIP error:', error);
      toast.error('Ошибка при экспорте ZIP архива');
    } finally {
      setIsExporting(false);
      setProgress(0);
    }
  };

  return (
    <Button
      onClick={handleExportZip}
      disabled={isExporting || checkedAccounts.length === 0}
      size={size}
      variant={variant}
      className={fullWidth ? 'w-full' : ''}
    >
      {isExporting ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Экспорт... {progress}%
        </>
      ) : (
        <>
          <Archive className="h-4 w-4" />
          Скачать ZIP ({checkedAccounts.length})
        </>
      )}
    </Button>
  );
}